import { CustomFieldDefinition, CustomFieldType, EmploymentType, ProcessingStatus } from '@prisma/client/browser'
import { z } from 'zod'
import moment, { MomentInput } from 'moment-timezone'
import { DropDownOptions, ICustomField } from '../types/types'
import { defaultChartFieldsConfig } from './customFields/defaultChartFieldsConfig'
import { fetchStorageAccessUrl, fetchStorageUploadCredential, type PublicUploadTarget } from '@/api/storage.api'
import { TIME_FORMAT } from '@/constants/time-format.constant'
import { typeErrors } from '@/utils/zod-type-errors.util'
export { filterCustomFieldsForGrid } from './gridFieldUtils'

export const convertVTTToChatHTML = (vttContent: string) => {
    // Split the content by line breaks to process each line (handle both \r\n and \n)
    const lines = vttContent?.split(/\r?\n/)
    let chatHtml = ''
    let currentSpeaker = ''
    let currentMessage = ''
    let currentTimestamp = ''
    let lastSpeaker = ''

    const formatVTTTimestamp = (ts: string): string => {
        // Convert HH:MM:SS.mmm to mm:ss
        const parts = ts.trim().split(':')
        if (parts.length === 3) {
            const hours = parseInt(parts[0], 10)
            const minutes = parseInt(parts[1], 10)
            const totalMinutes = hours * 60 + minutes
            const seconds = Math.floor(parseFloat(parts[2]))
            return `${totalMinutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        }
        return ts.trim()
    }

    const speakerColors = [
        { text: '#476be1', bg: '#f0f4ff', border: '#476be1' },
        { text: '#0d9488', bg: '#f0fdfa', border: '#0d9488' },
        { text: '#7c3aed', bg: '#f5f3ff', border: '#7c3aed' },
        { text: '#c2410c', bg: '#fff7ed', border: '#c2410c' },
        { text: '#0369a1', bg: '#f0f9ff', border: '#0369a1' },
    ]
    const speakerColorMap: Record<string, (typeof speakerColors)[0]> = {}
    let colorIndex = 0

    const getSpeakerColor = (speaker: string) => {
        if (!speakerColorMap[speaker]) {
            speakerColorMap[speaker] = speakerColors[colorIndex % speakerColors.length]
            colorIndex++
        }
        return speakerColorMap[speaker]
    }

    const userIcon = (color: string) =>
        `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`

    const flushMessage = () => {
        if (!currentMessage) return
        const cleanMessage = currentMessage.replace(/<\/v>/g, '').trim()
        if (!cleanMessage) return

        const colors = getSpeakerColor(currentSpeaker)
        chatHtml += `<div style="display:flex;gap:12px;align-items:flex-start;padding:10px 12px;border-bottom:1px solid #e5e7eb;border-left:3px solid ${colors.border};">
            <span style="font-family:monospace;font-size:12px;font-weight:600;color:${colors.text};min-width:44px;flex-shrink:0;padding-top:2px;">${currentTimestamp}</span>
            <div style="flex:1;min-width:0;">
                <span style="display:inline-flex;align-items:center;gap:4px;font-size:14px;font-weight:600;color:${colors.text};height:22px;margin-bottom:4px;">${userIcon(colors.text)} ${currentSpeaker}</span>
                <p style="font-size:14px;color:#374151;line-height:1.5;margin:0;">${cleanMessage}</p>
            </div>
        </div>`
        lastSpeaker = currentSpeaker
        currentMessage = ''
    }

    // Loop through each line to parse and convert to HTML
    lines?.forEach((line) => {
        if (line.startsWith('WEBVTT') || line === '') {
            // Skip the 'WEBVTT' header and blank lines
            return
        }

        if (line.includes('-->')) {
            // This is a timestamp line, so flush the previous message and extract start time
            flushMessage()
            const startTime = line.split('-->')[0]
            currentTimestamp = formatVTTTimestamp(startTime)
        } else if (line.startsWith('<v ')) {
            // This is a speaker line, extract the speaker's name
            const speakerMatch = line.match(/<v ([^>]+)>/)
            currentSpeaker = speakerMatch ? speakerMatch[1] : 'Unknown'
            currentMessage = line.replace(/<v [^>]+>/, '').replace(/<\/v>/g, '')
        } else {
            // This line is part of the message text
            currentMessage += ' ' + line.replace(/<\/v>/g, '')
        }
    })

    // Flush the last message if it exists
    flushMessage()

    return chatHtml
}

// Helper function to format dates with timezone in ISO format
export const formatDateTime = (dateStr: string, tzid: string) => {
    return moment.tz(dateStr, tzid).format().split('+')[0]
}

// Helper function to get end time
export const getEndTime = (startStr: string, duration: number, tzid: string) => {
    return moment.tz(startStr, tzid).add(duration, 'minutes').format().split('+')[0]
}

export const getPercentageToRating = (percentage: number): number => {
    const rawRating = percentage / 20 // Convert percentage to rating
    const roundedRating = Math.round(rawRating * 2) / 2 // Round to the nearest 0.5
    return Math.min(Math.max(roundedRating, 1), 5) // Ensure rating is between 1 and 5
}

// function to create manual date object using moment iso string and timezone
export const createManualDateObj = (date: MomentInput, tzid: string) => {
    const momentDate = moment.utc(date).tz(tzid)
    return new Date(momentDate.year(), momentDate.month(), momentDate.date(), momentDate.hour(), momentDate.minute(), momentDate.second())
}

const isAbortError = (err: unknown): boolean =>
    (err instanceof DOMException && err.name === 'AbortError') || (err instanceof Error && err.name === 'AbortError')

// Upload a file via provider-signed URL (Azure SAS / GCS V4). The server picks the object name under the
// company's folder; on anonymous pages pass the page's public upload target (job or talent-pool apply).
export const fileUpload = async (
    file: any,
    companySlug?: string,
    _jobApplicationId?: number,
    isWorkshort?: boolean,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal,
    publicTarget?: PublicUploadTarget
) => {
    try {
        if (signal?.aborted) {
            throw new DOMException('Upload aborted', 'AbortError')
        }
        if (companySlug === undefined || companySlug === null) {
            throw new Error('Company slug is undefined')
        }
        if (file) {
            const cred = await fetchStorageUploadCredential({
                companySlug,
                kind: isWorkshort ? 'introVideo' : 'document',
                fileExtension:
                    String(file?.name ?? '')
                        .split('.')
                        .pop() ?? '',
                contentType: file.type || undefined,
                publicTarget,
            })
            if (signal?.aborted) {
                throw new DOMException('Upload aborted', 'AbortError')
            }
            if (!cred.uploadUrl) {
                throw new Error('Missing uploadUrl from storage upload')
            }

            const isAzureUpload = /\.blob\.core\.windows\.net(?:\/|\?|$)/i.test(cred.uploadUrl)
            const headers: Record<string, string> = {
                'Content-Type': file.type || 'application/octet-stream',
            }
            // Azure Block Blob requires this header; sending it to GCS triggers a CORS preflight that fails.
            if (isAzureUpload) {
                headers['x-ms-blob-type'] = 'BlockBlob'
            }

            await new Promise<void>((resolve, reject) => {
                const xhr = new XMLHttpRequest()
                let settled = false
                const settleReject = (err: Error) => {
                    if (settled) return
                    settled = true
                    signal?.removeEventListener('abort', onAbort)
                    reject(err)
                }
                const settleResolve = () => {
                    if (settled) return
                    settled = true
                    signal?.removeEventListener('abort', onAbort)
                    resolve()
                }
                const onAbort = () => {
                    xhr.abort()
                    settleReject(new DOMException('Upload aborted', 'AbortError'))
                }
                if (signal) {
                    if (signal.aborted) {
                        onAbort()
                        return
                    }
                    signal.addEventListener('abort', onAbort, { once: true })
                }
                xhr.open('PUT', cred.uploadUrl, true)
                Object.entries(headers).forEach(([key, value]) => {
                    xhr.setRequestHeader(key, value)
                })
                xhr.upload.onprogress = (event) => {
                    if (!onProgress || !event.lengthComputable || event.total <= 0) return
                    onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)))
                }
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        onProgress?.(100)
                        settleResolve()
                        return
                    }
                    settleReject(new Error(`Upload failed with status ${xhr.status}`))
                }
                xhr.onerror = () => settleReject(new Error('Upload failed'))
                xhr.onabort = () => settleReject(new DOMException('Upload aborted', 'AbortError'))
                xhr.send(file)
            })

            return (cred.fileUrl || '').split('?')[0]
        }
    } catch (err) {
        if (isAbortError(err)) {
            throw err
        }
        console.error('Error uploading file: ', file, err)
        return ''
    }
}

export const getDuration = (startStr: string, endStr: string) => {
    return moment(endStr).diff(moment(startStr), 'minutes')
}

export const getFileAccessUrl = async (fileUrl: string) => {
    // Public pages receive already-signed URLs from their own endpoints — use them as they are.
    if (/[?&](sig|X-Goog-Signature)=/i.test(fileUrl)) return fileUrl
    try {
        return await fetchStorageAccessUrl(fileUrl)
    } catch (err) {
        console.error('Error getting access url: ', fileUrl, err)
        return ''
    }
}

export const capitalize = (text: string) => {
    if (typeof text !== 'string') return text
    if (!text) return ''
    if (text.indexOf('_') >= 0) {
        return text
            .split('_')
            .map((part) => part.at(0)?.toUpperCase() + part.substring(1, part.length).toLowerCase())
            .join(' ')
    }
    if (text === text.toUpperCase()) {
        return text.at(0)?.toUpperCase() + text.substring(1, text.length).toLowerCase()
    }
    return text.at(0)?.toUpperCase() + text.substring(1, text.length)
}

export const displayFileSize = (size: number) => {
    if (size < 1000000) {
        return (size / 1000).toFixed(2) + 'KB'
    } else {
        return (size / 1000000).toFixed(2) + 'MB'
    }
}

export const covertArrayToDropDownOptions = (array: any[]) => {
    return array.map((item) => {
        return {
            label: item.split('_').join('-'),
            value: item,
        } as DropDownOptions
    })
}

const getEmploymentTypeOptions = (type: EmploymentType, labelUpperCase: boolean = false) => {
    switch (type) {
        case EmploymentType.FULL_TIME:
            return { label: 'Full-time', value: EmploymentType.FULL_TIME }
        case EmploymentType.PART_TIME:
            return { label: 'Part-time', value: EmploymentType.PART_TIME }
        case EmploymentType.INTERNSHIP:
            return { label: 'Internship', value: EmploymentType.INTERNSHIP }
        case EmploymentType.TRAINEE:
            return { label: 'Trainee', value: EmploymentType.TRAINEE }
        default:
            return { label: labelUpperCase ? (type as unknown as string).toUpperCase() : capitalize(type), value: type }
    }
}

export function getFieldOptions(
    fieldName: string,
    customFields: CustomFieldDefinition[],
    standardFields?: string[] | number[],
    labelUpperCase = false
): DropDownOptions[] {
    const custom = customFields?.find((f) => f.fieldName === fieldName) as any

    if (fieldName === 'employmentType') {
        return (
            (standardFields
                ?.map((option: string | number) => getEmploymentTypeOptions(option as EmploymentType, labelUpperCase))
                .filter(Boolean) as DropDownOptions[]) || []
        )
    }

    if (custom && custom.fieldType === CustomFieldType.AUTOCOMPLETE && custom.fieldOptions?.length) {
        return custom.fieldOptions
    }

    if (!standardFields) {
        return []
    }

    // Fallback to standard fields
    return standardFields?.map((option: string | number) => {
        const label = typeof option === 'number' ? option.toString() : option.replace(/_/g, ' ')
        return {
            label: labelUpperCase ? label.toUpperCase() : capitalize(label),
            value: option,
        }
    })
}

export const rolesAndPermission = {
    External: {
        showReferButtonInJobList: false,
        jobApplicationDetail: false,
        createJob: false,
        adminAccess: false,
    },
    Employee: {
        talentPage: false,
        editCandidatePage: false,
        jobApplicationDetail: false,
        createJob: false,
        adminAccess: false,
    },
    Recruiter: {
        talentPage: true,
        editCandidatePage: true,
        jobApplicationDetail: true,
        createJob: true,
        adminAccess: false,
    },
    Manager: {
        talentPage: false,
        editCandidatePage: false,
        jobApplicationDetail: true,
        createJob: false,
        adminAccess: false,
    },
    Panellist: {
        talentPage: false,
        editCandidatePage: false,
        jobApplicationDetail: false,
        createJob: false,
        adminAccess: false,
    },
    HRHead: {
        talentPage: true,
        editCandidatePage: true,
        jobApplicationDetail: true,
        createJob: true,
        adminAccess: true,
    },
    Admin: {
        talentPage: true,
        editCandidatePage: true,
        jobApplicationDetail: true,
        createJob: true,
        adminAccess: true,
    },
    SuperAdmin: {
        talentPage: true,
        editCandidatePage: true,
        jobApplicationDetail: true,
        createJob: true,
        adminAccess: true,
    },
    TalentLead: {
        talentPage: true,
        editCandidatePage: true,
        jobApplicationDetail: true,
        createJob: true,
        adminAccess: false,
    },
}

export const checkRolesAndPermission = (role: string, feature: string) => {
    // Safely retrieve permission for the provided role/feature combination.
    // If the role is not present in the rolesAndPermission map, default to `false` to
    // avoid runtime errors when accessing undefined values.
    const rolePermissions = rolesAndPermission[role as keyof typeof rolesAndPermission]

    if (!rolePermissions) {
        // Unknown role – default to no access.
        return false
    }

    const permission = rolePermissions[feature as keyof typeof rolePermissions]
    return permission ?? false
}

export const isValidEmail = (value: string) => {
    const regExp = new RegExp(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)
    return regExp.test(value)
}

export const isValidUrl = (value: string) => {
    const regExp = new RegExp(/[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/)
    return regExp.test(value)
}

/**
 * Converts a domain to a full URL with protocol
 * @param domain - The domain string (e.g., "google.com" or "www.google.com")
 * @returns Full URL with https:// protocol (e.g., "https://google.com")
 * @example
 * getUrlFromDomain("google.com") // returns "https://google.com"
 * getUrlFromDomain("https://google.com") // returns "https://google.com"
 * getUrlFromDomain("http://example.com") // returns "http://example.com"
 */
export const getUrlFromDomain = (domain: string): string => {
    if (!domain || typeof domain !== 'string') {
        return domain
    }

    const trimmedDomain = domain.trim()

    // If it already has a protocol, return as is
    if (trimmedDomain.startsWith('http://') || trimmedDomain.startsWith('https://')) {
        return trimmedDomain
    }

    // Add https:// protocol
    return `https://${trimmedDomain}`
}

export const tabsForRole: any = {
    Recruiter: ['Dashboard', 'My Jobs', 'All Jobs', 'Talent'],
    Employee: ['All Jobs', 'Referrals'],
    Manager: ['My Jobs', 'All Jobs', 'Referrals'],
    External: ['All Jobs'],
    Admin: ['Dashboard', 'My Jobs', 'All Jobs', 'Talent'],
    SuperAdmin: ['Dashboard', 'My Jobs', 'All Jobs', 'Talent'],
    TalentManager: ['Dashboard', 'My Jobs', 'All Jobs', 'Talent'],
    HRHead: ['Dashboard', 'My Jobs', 'All Jobs', 'Talent'],
    TalentLead: ['Dashboard', 'My Jobs', 'All Jobs', 'Talent'],
}

export const tabToRoute: any = {
    Dashboard: 'recruitment/dashboard',
    'All Jobs': 'career/jobs',
    'My Jobs': 'recruitment/jobs/myJobs',
    //'Jobs Applied': 'career/jobs/jobsApplied',
    Referrals: 'recruitment/prot/referrals',
    Talent: 'recruitment/prot/talent',
}

export const routeTitleMap: any = {
    'recruitment/jobs/myJobs': 'My Jobs',
    'recruitment/dashboard': 'Insights',
    'recruitment/prot/talent': 'Talent',
    'recruitment/prot/referrals': 'Referrals',
    'career/jobs': 'Careers',
}

export function generateRandomId(length: number) {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    let counter = 0
    while (counter < length) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength))
        counter += 1
    }
    return result
}

export const customFieldStatus: Record<string, string> = {
    'Pre - Offer Stage': 'PRE_OFFER',
    'Offer Stage': 'OFFERED',
    Interview: 'INTERVIEW',
}

export const statusToBadge: { label: string; value: string; style: string }[] = [
    {
        label: 'Pending Review',
        value: 'PROFILE_REVIEW_PENDING',
        style: 'text-[14px] px-2 py-1 rounded-md bg-[#FFF3D0] text-[#C19105] font-medium',
    },
    {
        label: 'Shortlisted',
        value: 'SHORTLISTED',
        style: 'text-[14px] px-2 py-1 rounded-md bg-[#E8E2FE] text-[#7070D4] font-medium',
    },
    {
        label: 'Pre Offer',
        value: 'PRE_OFFER',
        style: 'text-[14px] px-2 py-1 rounded-md bg-orange-100 text-orange-500 font-medium',
    },
    {
        label: 'Offered',
        value: 'OFFERED',
        style: 'text-[14px] px-2 py-1 rounded-md bg-[#E9F6E8] text-[#42953C] font-medium',
    },
    {
        label: 'Interview',
        value: 'INTERVIEW',
        style: 'text-[14px] px-2 py-1 rounded-md bg-[#E9F6E8] text-[#42953C] font-medium',
    },
    {
        label: 'On Hold',
        value: 'ON_HOLD',
        style: 'text-[14px] px-2 py-1 rounded-md bg-[#FFF6E9] text-[#CF6D0B] font-medium',
    },
    {
        label: 'Rejected',
        value: 'REJECTED',
        style: 'text-[14px] px-2 py-1 rounded-md bg-[#FFBCBC] text-[#BE3535] font-medium',
    },
    {
        label: 'Offer Accepted',
        value: 'OFFER_ACCEPTED',
        style: 'text-[14px] px-2 py-1 rounded-md bg-[#E9F6E8] text-[#42953C] font-medium',
    },
    {
        label: 'Joined',
        value: 'JOINED',
        style: 'text-[14px] px-2 py-1 rounded-md bg-[#E9F6E8] text-[#1E7E34] font-medium',
    },
    {
        label: 'Joined',
        value: 'Joined',
        style: 'text-[14px] px-2 py-1 rounded-md bg-[#E9F6E8] text-[#1E7E34] font-medium',
    },
    {
        label: 'Offer Rejected',
        value: 'OFFER_REJECTED',
        style: 'text-[14px] px-2 py-1 rounded-md bg-[#FFBCBC] text-[#BE3535] font-medium',
    },
]

export const getFieldValueType = (fieldType: CustomFieldType) => {
    if (fieldType === CustomFieldType.SWITCH || fieldType === CustomFieldType.CHECKBOX) {
        return 'boolean'
    } else if (fieldType === CustomFieldType.NUMBER) {
        return 'number'
    } else {
        return 'string'
    }
}

export const getPlaceholder = (field: ICustomField, formData: any) => {
    // switch case for fieldName
    switch (field.fieldName) {
        case 'currentCompensation':
        case 'expectedCompensation':
            return 'Eg. 1,00,000'
        case 'assessmentTemplateId':
            return field.fieldOptions.length === 0 ? 'No templates available. Please create one.' : 'Select Assessment Template'
        case 'jobDescriptionId':
            return field.fieldOptions.length === 0 ? 'No job descriptions available. Please create one.' : 'Select Job Description'
        default:
            return field.fieldPlaceholder || (field.fieldType === CustomFieldType.AUTOCOMPLETE ? `Choose drop-down option` : `Enter value`)
    }
}

export { checkDisableNav } from './check-disable-nav.util'

// create a dynamic Zod schema for custom fields
export const createCustomFieldsSchema = (customFields: ICustomField[]): z.ZodObject<Record<string, z.ZodType<any>>> => {
    const schema: Record<string, z.ZodType<any>> = {}

    customFields.forEach((field) => {
        // Base validation depending on field type and whether it's required
        let fieldSchema: z.ZodType<any>

        const fieldLabel = field.fieldLabel || field.fieldName
        const isDropdown = field.fieldType === CustomFieldType.AUTOCOMPLETE || field.fieldType === 'AUTOCOMPLETE'
        const requiredStringMessage = isDropdown ? `${fieldLabel} is required` : `${fieldLabel} is required`

        switch (field.fieldValueType) {
            case 'string':
                fieldSchema = z.string().trim()
                if (field.isRequired) {
                    fieldSchema = (fieldSchema as z.ZodString).min(1, { message: requiredStringMessage })
                } else {
                    fieldSchema = fieldSchema.optional()
                }
                break

            case 'number':
                // Use "X is required" for empty/invalid so we don't show "must be a number" on number inputs
                fieldSchema = z.coerce.number(typeErrors({ required: `${fieldLabel} is required`, invalidType: `${fieldLabel} is required` }))
                if (field.fieldName === 'maxWorkExperience') {
                    fieldSchema = (fieldSchema as z.ZodNumber).min(0, { message: 'Cannot be less than 0' })
                }
                if (field.fieldName === 'noOfOpenings') {
                    fieldSchema = fieldSchema.refine((val) => val > 0, { message: 'Must be above 0' })
                }
                if (field.isRequired) {
                    fieldSchema = fieldSchema
                } else {
                    fieldSchema = fieldSchema.optional()
                }
                break

            case 'boolean':
                fieldSchema = z.boolean()
                if (!field.isRequired) {
                    fieldSchema = fieldSchema.optional()
                }
                break

            case 'date':
                fieldSchema = z
                    .string()
                    .trim()
                    .refine((val) => !isNaN(Date.parse(val)), { message: `${field.fieldLabel || field.fieldName} must be a valid date` })
                if (!field.isRequired) {
                    fieldSchema = fieldSchema.optional()
                }
                break

            default:
                fieldSchema = field.isRequired
                    ? z
                          .string()
                          .trim()
                          .min(1, { message: `${fieldLabel} is required` })
                    : z.string().trim().optional()
        }

        schema[field.fieldName] = fieldSchema
    })

    return z.object(schema)
}

export const getCustomLabel = (fieldName: string, defaultLabel: string, customFields: ICustomField[]) => {
    const field = customFields?.find((field: any) => field.fieldName === fieldName)
    return field ? field.fieldLabel : defaultLabel
}

export const getCustomValue = (row: any, fieldName: string, defaultValue: string = '') => {
    if (!row) return defaultValue || ''

    if (fieldName === 'subApplicationType') {
        if (row?.customFields?.applicationType) {
            switch (row?.customFields?.applicationType) {
                case 'EMPLOYEE_REFERRAL':
                    return `${row?.customFields?.refereeEmail ?? '-'}`
                case 'REFERRAL':
                    return `${row?.customFields?.refereeEmail ?? '-'}`
                case 'INTERNAL':
                    return `${row?.customFields?.internalEmployeeNumber ?? '-'}`
                case 'CONSULTANT':
                    return `${row?.customFields?.consultantEmail ?? '-'} from ${row?.customFields?.consultantCompanyName ?? '-'}`
                case 'BUSINESS_REFERRAL':
                    return `${row?.customFields?.businessLeaderName ?? '-'}`
                default:
                    const appType = row?.customFields?.['applicationType']
                    return appType ? String(appType).split('_').join(' ') : '-'
            }
        }
        return '-'
    }

    const customFieldValue = row?.customFields?.[fieldName] ?? defaultValue
    return customFieldValue !== undefined && customFieldValue !== null ? String(customFieldValue || '') : ''
}

export const getCustomFieldOptionsForFilter = (fieldName: string, customFields: ICustomField[], companySlug?: string) => {
    let options: any = []
    const field = customFields?.find((i: ICustomField) => i.fieldName === fieldName)
    if (field && field.fieldOptions && field.fieldOptions?.length) {
        options = field.fieldOptions
    } else if (field && field.fieldOptions && typeof field.fieldOptions === 'object') {
        // Handle department-based field options
        if (companySlug && field.dependsOnFieldName === 'departmentId') {
            // For department-dependent fields, flatten all options
            options = Object.values(field.fieldOptions).flat()
        } else {
            options = Object.values(field.fieldOptions).reduce((item: any, arr: any = []) => {
                return arr.concat(item.filter((item2: any) => !arr.some((item1: any) => item1.value === item2.value)))
            })
        }
    }
    return options.sort((a: any, b: any) => {
        return ('' + a.value).localeCompare(b.value)
    })
}

/**
 * Get field options for a specific field based on department ID
 * Handles department mapping from ID to name for custom field configurations
 */
export const getFieldOptionsForDepartment = (fieldName: string, customFields: ICustomField[], departmentId: string | number, departmentMap?: any) => {
    const field = customFields?.find((i: ICustomField) => i.fieldName === fieldName)
    if (!field?.fieldOptions) return []

    // If fieldOptions is an array, return as is
    if (Array.isArray(field.fieldOptions)) return field.fieldOptions

    // If fieldOptions is an object and depends on departmentId
    if (field.dependsOnFieldName === 'departmentId' && typeof field.fieldOptions === 'object') {
        const departmentName = departmentMap?.[departmentId]?.departmentName
        return departmentName ? field.fieldOptions[departmentName] || [] : []
    }

    // For other dependent fields, return empty array
    return []
}

// Enhanced whitelist with more fields and validation
export const ALLOWED_TABLES = defaultChartFieldsConfig
export const ALLOWED_AGGREGATIONS = ['count', 'sum', 'avg', 'min', 'max', 'distinct_count']
export const ALLOWED_DATE_FUNCTIONS = ['date_trunc', 'extract']
export const ALLOWED_DATE_PARTS = ['year', 'month', 'day', 'week', 'quarter']

/**
 * Changes camel case to a human readable format. So helloWorld, hello-world and hello_world becomes "Hello World".
 * */
export function prettifyCamelCase(str: string = '') {
    var output = ''
    var len = str.length
    var char

    for (var i = 0; i < len; i++) {
        char = str.charAt(i)

        if (i == 0) {
            output += char.toUpperCase()
        } else if (char !== char.toLowerCase() && char === char.toUpperCase()) {
            output += ' ' + char
        } else if (char == '-' || char == '_') {
            output += ' '
        } else {
            output += char
        }
    }

    return output
}

class AbortError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'AbortError'
    }
}

/**
 * Polls the backend API individually for each upload ID until all reach 100% processing status.
 * Uses a user-provided handler to process each API response.
 * Ensures one batch of requests completes before starting the next polling cycle with exponential backoff.
 * Supports cancellation via AbortSignal to prevent overlapping polling instances.
 *
 * @param id - Id to poll.
 * @param handleStatusUpdate - Handler function to process the API response for each ID.
 * @param onComplete - Callback function to call when all IDs are complete.
 * @param signal - Optional AbortSignal to cancel the polling.
 * @param initialPollInterval - Initial polling interval in milliseconds (default: 5000).
 * @param apiBaseUrl - Base URL for the status API (appended with /:id, default: '/api/status').
 * @returns Promise that resolves when all polling is complete or rejects on critical error or abort.
 */
export async function pollUntilAllComplete({
    id,
    handleStatusUpdate,
    onComplete,
    pollInterval,
    entityType,
    companySlug,
}: {
    id: number
    handleStatusUpdate: (id: number, data: any) => void
    onComplete: () => void
    pollInterval: number
    entityType: 'JobApplication' | 'Candidate'
    companySlug: string
}): Promise<void> {
    // Helper function for exponential backoff delay
    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

    console.log(`Starting polling cycle for Id: ${id}`)

    // Create a single batch of requests for all pending IDs
    try {
        await delay(pollInterval)
        // Refactored URL construction
        const statusUrl = entityType === 'JobApplication' ? `/api/jobApplications/${id}` : `/api/candidates/${id}?companySlug=${companySlug}`
        console.log(`Fetching status for ID ${id} at ${statusUrl}`)

        const response = await fetch(statusUrl)
        if (!response.ok) {
            handleStatusUpdate(id, { id, processingStatus: 'DUPLICATE', resumeUrl: 'INVALID_RESUME' })
            throw new Error(`Failed to fetch status for ID ${id}: ${response.statusText}`)
        }
        const resData = await response.json()
        let data = null
        if (resData.processingStatus === undefined && resData.data) {
            data = resData.data
        } else {
            data = resData
        }
        let retries = 0
        while (
            [ProcessingStatus.INIT, ProcessingStatus.WORKING].includes(data.processingStatus) &&
            data.processingStatus !== ProcessingStatus.BASIC_PROFILE_EXTRACTED
        ) {
            console.log(`ID ${id} processing at ${data.processingStatus}%. Continuing to poll...`)
            await delay(pollInterval)
            try {
                const repeatResponse = await fetch(statusUrl)
                if (!repeatResponse.ok) {
                    handleStatusUpdate(id, { id, processingStatus: 'DUPLICATE', resumeUrl: 'Duplicate Profile' })
                    throw new Error(`Failed to fetch status for ID ${id}: ${repeatResponse.statusText}`)
                }
                const repeatResData = await repeatResponse.json()
                if (repeatResData.processingStatusProgress === undefined && repeatResData.data) {
                    data = repeatResData.data
                } else {
                    data = repeatResData
                }
            } catch (error) {
                retries += 1
                console.error(`Error polling ID ${id} on retry ${retries}:`, error)
                if (retries >= 3) {
                    console.error(`Max retries reached for ID ${id}. Exiting polling.`)
                    break
                }
            }
        }
        // Call the user-provided handler to process the response
        handleStatusUpdate(id, data)
        // Remove from pending if processing is complete
        if (
            data.processingStatus === ProcessingStatus.BASIC_PROFILE_EXTRACTED ||
            data.processingStatus === ProcessingStatus.PROCESSED ||
            data.processingStatus === ProcessingStatus.ERROR ||
            data.processingStatus === ProcessingStatus.DUPLICATE
        ) {
            console.log(`ID ${id} completed processing with status: ${data.processingStatus}`)
            onComplete()
        }
    } catch (error: any) {
        console.error(`Error polling ID ${id}:`, error)
        onComplete()
    }
}

export const isVideoFile = (file: any) => {
    if (!file) return false
    // For uploaded File objects, prefer MIME type
    if (file.type && typeof file.type === 'string') {
        if (file.type.startsWith('video/')) return true
    }
    // Fallback to extension check
    const name = (file.name || file.fileName || '') as string
    return /\.(mp4|mov|webm|m4v|ogg|hevc|3gp|3gp2|mpg|mpeg|mpg4|mp4v)$/i.test(name)
}

export const populateVideoDuration = (file: File, duration: number = 120) =>
    new Promise((resolve, reject) => {
        try {
            let video = document.createElement('video')
            video.style.display = 'none'
            video.preload = 'metadata'

            video.onloadedmetadata = function () {
                window.URL.revokeObjectURL(video.src)
                console.log('Adding video duration: ', file, video.duration, duration)
                Object.defineProperty(file, 'duration', { value: video.duration, enumerable: false })
                if (video.duration && video.duration > duration) {
                    console.log(`Invalid Video! video is more than ${duration} seconds`)
                    reject(false)
                }
                resolve(true)
            }

            video.onerror = function () {
                console.log("Error! Couldn't load video. Invalid file")
                reject(false)
            }

            video.src = window.URL.createObjectURL(file)
        } catch (e) {
            reject(e)
        }
    })

// Utility to sort GridColDef fields based on fixedStart order ( Biz2 requirement )
export function sortFields(fixedStart: string[] = ['lineNo', 'id', 'firstName', 'score'], fieldList: string[]): string[] {
    const priorityAfterScore = ['customFields.applicationStatus', 'customFields.applicationSubStatus']

    // Create a map for quick lookup of priority positions
    const priorityIndex = new Map()

    // 1. First group: original order until "score"
    fixedStart.forEach((field, index) => {
        priorityIndex.set(field, index)
    })

    // 2. Second group: right after score
    priorityAfterScore.forEach((field, idx) => {
        priorityIndex.set(field, fixedStart.length + idx)
    })

    return [...fieldList].sort((a, b) => {
        // If both are in priority map, compare their positions
        const aIndex = priorityIndex.has(a) ? priorityIndex.get(a) : Infinity
        const bIndex = priorityIndex.has(b) ? priorityIndex.get(b) : Infinity

        if (aIndex !== bIndex) {
            return aIndex - bIndex
        }

        // For fields with same priority (i.e. both Infinity = rest group),
        // keep original order by using their original index
        return fieldList.indexOf(a) - fieldList.indexOf(b)
    })
}

// Utility to sort GridColDef fields based on fixedStart order ( Biz2 requirement )
export function sortFieldObjects(
    fixedStart: string[] = ['lineNo', 'id', 'firstName', 'score'],
    fieldObjects: { field: string; [key: string]: any }[]
): { field: string; [key: string]: any }[] {
    const sortedFields = sortFields(
        fixedStart,
        fieldObjects.map((obj) => obj.field)
    )
    return sortedFields
        .map((field) => fieldObjects.find((obj) => obj.field === field))
        .filter((obj): obj is { field: string; [key: string]: any } => obj !== undefined)
}

/**
 * Given a full JobPosting API response (including `customFields` JSON and standard fields),
 * returns an array of grouped display sections formatted for use in AcceptanceComponent.
 *
 * Uses the Benetton `JobPosting` config sections to determine grouping, labels, and
 * value-to-label mapping for AUTOCOMPLETE fields.
 *
 * Only relevant for Benetton jobs – call this when `companySlug === 'benetton'`
 * or when the job has Benetton-specific custom fields (e.g. `businessDepartment`).
 */
export function formatBenettonJobCustomFields(jobPosting: Record<string, any>): { title: string; fields: { label: string; value: string }[] }[] {
    // Inline import is safe here because this is a utility used only client-side
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const benettonConfig = require('../../config/form-fields/benetton.json')
    const sections: { id: number; title: string; fields: any[] }[] = benettonConfig.JobPosting ?? []
    const rawCustomFields: Record<string, any> = (jobPosting?.customFields as Record<string, any>) ?? {}

    /** Skipped field types that don't add meaningful display info */
    const SKIP_TYPES = new Set(['FILEUPLOAD', 'RICHTEXT'])

    const resolveDisplayValue = (field: any): string | null => {
        const { fieldName, fieldType, fieldOptions, isExistingField, isExistingFieldHidden, isHiddenFromGrid, isVisible } = field

        // Skip hidden fields
        if (isExistingFieldHidden || isHiddenFromGrid || isVisible === false) return null
        // Skip non-displayable field types
        if (SKIP_TYPES.has(fieldType)) return null

        // Resolve raw value: existing fields live on the jobPosting root, custom fields live in customFields
        let rawValue = isExistingField ? jobPosting?.[fieldName] : rawCustomFields?.[fieldName]

        // Handle relation mapping so we display Names, not IDs
        if (fieldName === 'departmentId' && jobPosting?.Department?.departmentName) {
            rawValue = jobPosting.Department.departmentName
        } else if (fieldName === 'assessmentTemplateId' && jobPosting?.AssessmentTemplate?.name) {
            rawValue = jobPosting.AssessmentTemplate.name
        }

        if (rawValue === null || rawValue === undefined || rawValue === '') return null

        // AUTOCOMPLETE: map stored value → option label
        if (fieldType === 'AUTOCOMPLETE' && fieldOptions) {
            if (Array.isArray(fieldOptions)) {
                const match = fieldOptions.find((opt: any) => String(opt.value) === String(rawValue))
                if (match) return match.label
            } else if (typeof fieldOptions === 'object') {
                // Nested fieldOptions (dependent on another field)
                for (const group of Object.values(fieldOptions)) {
                    if (Array.isArray(group)) {
                        const match = (group as any[]).find((opt: any) => String(opt.value) === String(rawValue))
                        if (match) return match.label
                    }
                }
            }
            // Fallback: prettify the raw value
            return String(rawValue).replace(/_/g, ' ')
        }

        // SWITCH / BOOLEAN
        if (fieldType === 'SWITCH' || typeof rawValue === 'boolean') {
            return rawValue === true || rawValue === 'true' ? 'Yes' : 'No'
        }

        // DATE
        if (fieldType === 'DATE') {
            const parsed = moment(rawValue)
            return parsed.isValid() ? parsed.format(TIME_FORMAT.DATE_MEDIUM) : String(rawValue)
        }

        return String(rawValue)
    }

    const result: { title: string; fields: { label: string; value: string }[] }[] = []

    for (const section of sections) {
        if (section.title === 'Interview/Assessment Details') continue

        const displayFields: { label: string; value: string }[] = []

        const sorted = [...(section.fields ?? [])].sort((a: any, b: any) => (a.placementOrdering ?? 0) - (b.placementOrdering ?? 0))

        for (const field of sorted) {
            const displayValue = resolveDisplayValue(field)
            if (displayValue === null) continue
            displayFields.push({ label: field.fieldLabel, value: displayValue })
        }

        if (displayFields.length > 0) {
            result.push({ title: section.title, fields: displayFields })
        }
    }

    return result
}
